import { GraphQLSchema } from 'graphql';
import * as monaco from 'monaco-editor';

import {
  getDocumentDefinitions,
  getPathToOffset,
} from 'src/app/graph/explorerPage/helpers/graphqlParseHelpers';
import { operationPathToGraphQLPath } from 'src/app/graph/explorerPage/helpers/operationPathHelpers';
import { getRootTypeNames, unwrapType } from 'src/lib/graphql/graphqlHelpers';

/**
 * Factory function to create `HoverProvider`.
 */
export function schemaHoverProviderFactory({
  schemaRef,
}: {
  schemaRef: React.RefObject<GraphQLSchema | undefined>;
}): monaco.languages.HoverProvider {
  return {
    /**
     * Provide hover items for a given position.
     *
     * The following was copied from `languageProvider.provideHover`
     * (https://github.com/apollographql/apollo-tooling/blob/c8f2e923e3bb722341643c1a5bf31b6e14df54eb/packages/apollo-language-server/src/languageProvider.ts#L262)
     * with slight modifications
     */
    async provideHover(
      model,
      position,
    ): Promise<null | monaco.languages.Hover> {
      const schema = schemaRef.current;
      if (!schema) {
        return null;
      }

      const document = model.getValue();

      const pathToOffset = getPathToOffset(model, position);
      if (!pathToOffset) {
        // No path, do nothing
        return null;
      }

      const { path, endState, hoverRange } = pathToOffset;
      switch (endState.kind) {
        // FIELD
        case 'Field':
        case 'AliasedField': {
          // Needs full path to find field, use monacoUriToDocNodes or something
          // similar
          const docPath = path && operationPathToGraphQLPath(path, schema);
          const [parent, fieldDef] = docPath?.slice(-2) ?? [];
          if (!parent || !fieldDef || !('args' in fieldDef)) {
            return null;
          }
          const parentType = unwrapType(parent);

          const argsString =
            fieldDef.args.length > 0
              ? `(${fieldDef.args
                  .map((a) => `${a.name}: ${a.type}`)
                  .join(', ')})`
              : '';

          const content = [
            [
              `\`\`\`graphql`,
              `${parentType.name}.${fieldDef.name}${argsString}: ${fieldDef.type}`,
              `\`\`\``,
            ].join('\n'),
          ];

          const info: string[] = [];

          if (info.length !== 0) {
            content.push(info.join(' '));
          }

          if (fieldDef.description) {
            content.push(fieldDef.description);
          }

          return {
            contents: [{ value: content.join('\n\n---\n\n') }],
            range: hoverRange,
          };
        }
        // FRAGMENT_SPREAD
        case 'FragmentSpread': {
          // Go to look up fragment
          const { fragmentDefinitions } = getDocumentDefinitions(
            document,
            getRootTypeNames(schema),
          );
          const fragmentName = endState.name;
          const fragment = fragmentDefinitions.find(
            (definition) => definition.name === fragmentName,
          );
          if (fragment) {
            return {
              contents: [
                {
                  value: [
                    `\`\`\`graphql`,
                    `fragment ${fragmentName} on ${fragment.type}`,
                    `\`\`\``,
                  ].join('\n'),
                },
              ],
            };
          }
          return null;
        }
        // NAMED_TYPE
        case 'InlineFragment':
        case 'NamedType': {
          const typeName =
            endState.kind === 'InlineFragment' ? endState.type : endState.name;
          const type = schema.getType(typeName ?? '');
          if (!type) {
            return null;
          }

          const content = [[`\`\`\`graphql`, `${type}`, `\`\`\``].join('\n')];

          if (type.description) {
            content.push(type.description);
          }

          return {
            contents: [{ value: content.join('\n\n---\n\n') }],
            range: hoverRange,
          };
        }
        // ARGUMENT
        case 'Argument': {
          const docPath = path && operationPathToGraphQLPath(path, schema);
          if (!docPath) {
            return null;
          }
          const field = docPath.slice(-1)[0];
          if (!field || !('args' in field)) {
            return null;
          }
          const argument = field.args.find((arg) => arg.name === endState.name);

          if (!argument) {
            return null;
          }
          const content = [
            [
              `\`\`\`graphql`,
              `${argument.name}: ${argument.type}`,
              `\`\`\``,
            ].join('\n'),
          ];
          if (argument.description) {
            content.push(argument.description);
          }
          return {
            contents: [{ value: content.join('\n\n---\n\n') }],
            range: hoverRange,
          };
        }
        // DIRECTIVE
        case 'Directive': {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const directive = schema.getDirective(endState.name!);
          if (!directive) {
            return null;
          }

          const argsString =
            directive.args.length > 0
              ? `(${directive.args
                  .map((a) => `${a.name}: ${a.type}`)
                  .join(', ')})`
              : '';
          const content = [
            [`\`\`\`graphql`, `@${directive.name}${argsString}`, `\`\`\``].join(
              '\n',
            ),
          ];
          if (directive.description) {
            content.push(directive.description);
          }
          return {
            contents: [{ value: content.join('\n\n---\n\n') }],
            range: hoverRange,
          };
        }
        default:
          return null;
      }
    },
  };
}
